import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import MembershipCard from '../../components/payment/MembershipCard';
import StripeProvider from '../../components/payment/StripeProvider';
import { membershipPlans } from '../../data/membershipPlans';
import ApiService from '../../services/apiService';

const Membership = () => {
    const { currentUser } = useAuth();
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkSubscriptionStatus = async () => {
            if (currentUser) {
                try {
                    const token = await currentUser.getIdToken();
                    const status = await ApiService.getSubscriptionStatus(currentUser.uid, token);
                    setSubscriptionStatus(status);
                } catch (error) {
                    console.error('Error checking subscription status:', error);
                }
            }
            setLoading(false);
        };

        checkSubscriptionStatus();
    }, [currentUser]);

    const handlePaymentSuccess = (plan) => {
        console.log('Payment successful for plan:', plan);
        // You can add additional logic here like showing a success message
        // or redirecting to a success page
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Membership Plans - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Choose from our flexible membership plans: 15 minutes, 1 hour, or 15 hours of AI tutoring sessions." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main>
                {/* Hero Section */}
                <section className="pt-28 pb-20 bg-gradient-to-b from-white to-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-5xl py-10 text-primary">|</h1>
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">
                                Choose Your <span className="text-primary">Learning Journey</span>
                            </h1>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                                Select the perfect membership plan for your learning needs. From quick sessions to intensive programs, we have options for every learner.
                            </p>
                            
                            {subscriptionStatus && subscriptionStatus.active && (
                                <div className="bg-green-100 border border-green-300 rounded-lg p-4 max-w-md mx-auto">
                                    <p className="text-green-800 font-medium">
                                        ✅ You have an active subscription
                                    </p>
                                    <p className="text-green-600 text-sm mt-1">
                                        Current plan: {subscriptionStatus.planName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Membership Plans Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <StripeProvider>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {membershipPlans.map((plan) => (
                                    <MembershipCard 
                                        key={plan.id} 
                                        plan={plan} 
                                        onPaymentSuccess={handlePaymentSuccess}
                                    />
                                ))}
                            </div>
                        </StripeProvider>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Why Choose Our Memberships?
                            </h2>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                Our flexible membership system allows you to learn at your own pace with world-class AI tutoring.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">⚡</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Learning</h3>
                                <p className="text-gray-600">
                                    Choose from 15-minute quick sessions to 15-hour intensive programs based on your schedule.
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">🤖</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Powered</h3>
                                <p className="text-gray-600">
                                    Advanced AI tutors that adapt to your learning style and provide personalized instruction.
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">📊</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Tracking</h3>
                                <p className="text-gray-600">
                                    Detailed analytics and progress reports to help you track your learning journey.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Frequently Asked Questions
                            </h2>
                        </div>

                        <div className="space-y-8">
                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    Can I change my membership plan?
                                </h3>
                                <p className="text-gray-600">
                                    Yes, you can upgrade or downgrade your membership plan at any time. Changes will be reflected in your next billing cycle.
                                </p>
                            </div>

                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    What happens if I don't use all my session time?
                                </h3>
                                <p className="text-gray-600">
                                    Unused session time doesn't roll over to the next period. We recommend choosing a plan that matches your learning schedule.
                                </p>
                            </div>

                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    Is there a money-back guarantee?
                                </h3>
                                <p className="text-gray-600">
                                    Yes, we offer a 30-day money-back guarantee for all membership plans. If you're not satisfied, contact our support team.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default Membership;
